let judgeMeLoadPromise = null;

const revealJudgeMeReviews = () => {
  document.querySelectorAll('[data-product-reviews]').forEach((widget) => {
    widget.querySelectorAll('.jdgm-temp-hiding-style').forEach((style) => style.remove());
    widget.querySelectorAll('.jdgm-rev-widg').forEach((reviews) => {
      reviews.style.removeProperty('display');
    });
  });
};

const loadDeferredJudgeMe = () => {
  if (!window.aveyDeferJudgeMe || window.jdgm?._doneSetup) {
    return Promise.resolve(true);
  }
  if (judgeMeLoadPromise) return judgeMeLoadPromise;

  const originalLoader = [...document.scripts].find(
    (script) => script.src.includes('/judgeme-') && script.src.includes('/assets/loader.js'),
  );

  if (!originalLoader) {
    window.aveyDeferJudgeMe = false;
    revealJudgeMeReviews();
    return Promise.resolve(false);
  }

  judgeMeLoadPromise = new Promise((resolve) => {
    const loader = document.createElement('script');
    let fallbackTimer = null;
    let settled = false;

    const finish = (loaded) => {
      if (settled) return;
      settled = true;
      if (fallbackTimer) window.clearTimeout(fallbackTimer);
      document.removeEventListener('jdgm.doneSetup', onSetup);
      window.aveyDeferJudgeMe = false;
      if (!loaded) revealJudgeMeReviews();
      resolve(loaded);
    };
    const onSetup = () => finish(true);

    document.addEventListener('jdgm.doneSetup', onSetup, { once: true });
    loader.src = originalLoader.src;
    loader.async = true;
    loader.dataset.aveyJudgeMeLoader = '';
    loader.onload = () => {
      fallbackTimer = window.setTimeout(() => finish(true), 3000);
    };
    loader.onerror = () => finish(false);

    window.jdgm = window.jdgm || {};
    window.jdgm._loaderExecuted = false;
    document.body.append(loader);
  });

  return judgeMeLoadPromise;
};

class AveyProductReviews {
  constructor(widget) {
    this.widget = widget;
    this.productId = widget.dataset.id;
    this.shopDomain = widget.dataset.shopDomain;
    this.locale = widget.dataset.locale || document.documentElement.lang || 'fr';
    this.loadingLabel = widget.dataset.loadingLabel || '';
    this.sortLabel = widget.dataset.sortLabel || '';
    this.verifiedLabel = widget.dataset.verifiedLabel || '';
    this.productTitle = widget.dataset.productTitle || '';
    this.emptyReviewLabel = widget.dataset.emptyReviewLabel || '';
    this.ratingLabel = widget.dataset.ratingLabel || '__RATING__ out of 5';
    this.paginationLabel = widget.dataset.paginationLabel || 'Review pages';
    this.previousLabel = widget.dataset.previousLabel || 'Previous';
    this.nextLabel = widget.dataset.nextLabel || 'Next';
    this.pageStatusTemplate = widget.dataset.pageStatusTemplate || 'Page __CURRENT__ of __TOTAL__';
    this.controller = null;
    this.heightReleaseFrame = null;
    this.enhancementFrame = null;
    this.filterScrollTimer = null;
    this.judgeMeScrollConfigured = false;
    this.judgeMeReady = Promise.resolve(false);
    this.originalReviews = new WeakMap();
    this.onClick = this.onClick.bind(this);
    this.requestedWrite = new URLSearchParams(window.location.search).get('review') === 'write';
  }

  connect() {
    if (!this.productId || !this.shopDomain) return;

    this.widget.addEventListener('click', this.onClick, true);
    this.configureJudgeMeScroll();
    this.syncCustomSummary();
    this.enhanceReviews();
    this.enhancePagination();
    this.localizeDates();
    this.observeJudgeMeUpdates();
    this.judgeMeReady = this.refreshMissingReviewLanguages().then(loadDeferredJudgeMe);
    this.enhanceReviewForm();

    this.judgeMeReady.then((loaded) => {
      this.openRequestedReview();
      if (!loaded) return;
      this.configureJudgeMeScroll();
      this.initializeJudgeMeFeatures();
      this.enhanceReviews();
      this.enhancePagination();
      this.localizeDates();
    });
  }

  openRequestedReview() {
    if (!this.requestedWrite) return;
    const form = this.widget.querySelector('.jdgm-form-wrapper');
    if (!form) return;
    this.requestedWrite = false;
    form.style.display = 'block';
    this.widget.querySelector('[data-avey-write-review]')?.setAttribute('aria-expanded', 'true');
    this.scrollToReviewForm(form);
  }

  scrollToReviewForm(form) {
    const title = form.querySelector('.jdgm-form__title');
    const sectionHeading = this.widget.closest('[data-product-information-kind]')?.querySelector('.avey-product-tabs__heading');
    form.style.setProperty('--avey-review-heading-height', `${Math.ceil(sectionHeading?.getBoundingClientRect().height || 0)}px`);
    if (title) {
      title.tabIndex = -1;
      title.focus({ preventScroll: true });
    }
    const tabs = this.widget.closest('avey-product-tabs');
    if (tabs?.scrollToSection) {
      tabs.scrollToSection(title || form, { anticipateAnnouncementCollapse: true });
    } else {
      (title || form).scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  }

  enhanceReviewForm() {
    const reviewerName = this.widget.querySelector('input[name="reviewer_name"]');
    if (reviewerName?.hasAttribute('placeholder')) reviewerName.removeAttribute('placeholder');
    const nameFormat = this.widget.querySelector('.jdgm-form__reviewer-name-format-container');
    nameFormat?.childNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE && /^[\s()]+$/.test(node.textContent)) node.remove();
    });
    if (document.documentElement.lang.split('-')[0] !== 'fr' || reviewerName?.value.trim()) return;
    const examples = { 'John Smith': 'Jean Dupont', 'John S.': 'Jean D.', John: 'Jean', 'J.S.': 'J.D.' };
    this.widget.querySelectorAll('select[name="reviewer_name_format"] option').forEach((option) => {
      const replacement = examples[option.textContent.trim()];
      if (replacement) option.textContent = replacement;
    });
  }

  async onClick(event) {
    const writeReview = event.target.closest('[data-avey-write-review]');
    if (writeReview) {
      event.preventDefault();
      await this.judgeMeReady;
      const formWrapper = this.widget.querySelector('.jdgm-form-wrapper');
      if (!formWrapper) return;

      const shouldOpen = window.getComputedStyle(formWrapper).display === 'none';
      formWrapper.style.display = shouldOpen ? 'block' : 'none';
      writeReview.setAttribute('aria-expanded', String(shouldOpen));
      if (shouldOpen) {
        this.enhanceReviewForm();
        this.scrollToReviewForm(formWrapper);
      }
      return;
    }

    if (event.target.closest('.jdgm-cancel-rev')) {
      this.widget.querySelector('[data-avey-write-review]')?.setAttribute('aria-expanded', 'false');
    }

    const ratingFilter = event.target.closest('[data-avey-rating-filter]');
    if (ratingFilter) {
      event.preventDefault();
      await this.judgeMeReady;
      const rating = ratingFilter.dataset.aveyRatingFilter;
      const nativeFilter = this.widget.querySelector(`.jdgm-histogram__row[data-rating="${rating}"] .jdgm-histogram__star`);
      if (nativeFilter) {
        const usesCustomScroll = this.configureJudgeMeScroll();
        if (!usesCustomScroll) this.scrollAfterRatingFilter();
        nativeFilter.click();
      }
      this.widget.querySelectorAll('[data-avey-rating-filter]').forEach((row) => {
        row.setAttribute('aria-pressed', String(row === ratingFilter));
      });
      return;
    }

    const pageLink = event.target.closest('.jdgm-paginate__page[data-page]');
    if (!pageLink || !this.widget.contains(pageLink)) return;

    const page = Number.parseInt(pageLink.dataset.page, 10);
    const currentPage = Number.parseInt(
      this.widget.querySelector('.jdgm-paginate')?.dataset.currentPage ||
        this.widget.querySelector('.jdgm-paginate__page.jdgm-curt')?.dataset.page ||
        '1',
      10,
    );

    if (!Number.isFinite(page) || page < 1 || page === currentPage) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.loadPage(page);
  }

  syncCustomSummary() {
    const summary = this.widget.querySelector('[data-avey-reviews-summary]');
    if (!summary) return;

    this.widget.querySelectorAll('.jdgm-histogram__row[data-rating]').forEach((nativeRow) => {
      const rating = nativeRow.dataset.rating;
      const count = nativeRow.dataset.frequency || '0';
      const percentage = Math.max(0, Math.min(100, Number(nativeRow.dataset.percentage) || 0));
      const row = summary.querySelector(`[data-avey-rating-filter="${rating}"]`);
      if (!row) return;

      const labelTemplate = row.dataset.labelTemplate || '__RATING__: __COUNT__';
      row.setAttribute(
        'aria-label',
        labelTemplate.replace('__RATING__', rating).replace('__COUNT__', count),
      );
      row.querySelector('[data-avey-rating-count]').textContent = count;
      row.querySelector('[data-avey-rating-bar]').style.width = `${percentage}%`;
    });
  }

  reviewText(review) {
    const text = (selector) => (review.querySelector(selector)?.textContent || '')
      .normalize('NFC').replace(/\s+/g, ' ').trim();
    return { title: text('.jdgm-rev__title'), body: text('.jdgm-rev__body') };
  }

  reviewsEndpoint(page, perPage = 5) {
    const endpoint = new URL('https://cdn.judge.me/reviews/reviews_for_widget');
    endpoint.search = new URLSearchParams({
      url: this.shopDomain,
      shop_domain: this.shopDomain,
      platform: 'shopify',
      page: String(page),
      per_page: String(perPage),
      product_id: this.productId,
    });
    return endpoint;
  }

  async refreshMissingReviewLanguages() {
    if (window.jdgm?._doneSetup) return;
    const missing = [...this.widget.querySelectorAll('.jdgm-rev')]
      .filter((review) => review.dataset.reviewId && !review.dataset.reviewLanguage);
    if (!missing.length) return;

    // The initial Shopify metafield can lag behind Judge.me's language detection.
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 2000);
    try {
      const response = await fetch(this.reviewsEndpoint(1), {
        credentials: 'omit', signal: controller.signal,
      });
      if (!response.ok) return;
      const payload = await response.json();
      if (typeof payload.html !== 'string') return;
      const template = document.createElement('template');
      template.innerHTML = payload.html;
      const languages = new Map([...template.content.querySelectorAll('.jdgm-rev')]
        .map((review) => [review.dataset.reviewId, review.dataset.reviewLanguage]));
      missing.forEach((review) => {
        const language = languages.get(review.dataset.reviewId);
        if (language) review.dataset.reviewLanguage = language;
      });
    } catch {
      // Keep native translation available if the API or detection is not ready.
    } finally {
      window.clearTimeout(timeout);
    }
  }

  isShowingTranslation(button) {
    return Boolean(button && window.jdgm?.$?.(button).data('showing-translation'));
  }

  rememberOriginalReviews(root = this.widget) {
    root.querySelectorAll('.jdgm-rev').forEach((review) => {
      if (this.originalReviews.has(review)) return;
      // Capture before native translation, including newly paginated/filtered reviews.
      if (this.isShowingTranslation(review.querySelector('.jdgm-translate-button'))) return;
      this.originalReviews.set(review, this.reviewText(review));
    });
  }

  updateTranslationControl(review) {
    const container = review.querySelector('.jdgm-translate-container');
    if (!container) return;
    const original = this.originalReviews.get(review);
    const current = this.reviewText(review);
    const redundant = Boolean(original
      && this.isShowingTranslation(review.querySelector('.jdgm-translate-button'))
      && current.title === original.title && current.body === original.body);

    // Judge.me can translate an undetected language into the very same text.
    // Keep native controls for real translations, pending translations and errors.
    if (container.hidden !== redundant) container.hidden = redundant;
  }

  enhanceReviews() {
    this.rememberOriginalReviews();
    const nativeTitle = this.widget.querySelector('.jdgm-rev-widg__title');
    if (nativeTitle?.tagName === 'H2') {
      const neutralTitle = document.createElement('div');
      neutralTitle.className = nativeTitle.className;
      neutralTitle.setAttribute('aria-hidden', 'true');
      neutralTitle.innerHTML = nativeTitle.innerHTML;
      nativeTitle.replaceWith(neutralTitle);
    }

    const sortLabel = this.widget.querySelector('.jdgm-sort-dropdown-label');
    const sortWrapper = sortLabel?.closest('.jdgm-sort-dropdown-wrapper');
    if (sortWrapper && sortLabel) {
      sortWrapper.classList.add('avey-reviews-sort');
      sortLabel.removeAttribute('style');
      if (sortLabel.textContent !== this.sortLabel) sortLabel.textContent = this.sortLabel;
    }

    this.widget.querySelectorAll('.jdgm-rev').forEach((review) => {
      review.classList.add('avey-review-card');

      const rating = review.querySelector('.jdgm-rev__rating');
      const score = Math.max(0, Math.min(5, Number.parseInt(rating?.dataset.score || '0', 10)));
      if (rating && !rating.querySelector('.avey-review-card__stars')) {
        const stars = document.createElement('span');
        stars.className = 'avey-review-card__stars';
        stars.setAttribute('aria-hidden', 'true');
        stars.textContent = `${'★'.repeat(score)}${'☆'.repeat(5 - score)}`;
        rating.replaceChildren(stars);
        rating.setAttribute('aria-label', this.ratingLabel.replace('__RATING__', String(score)));
      }

      const buyerBadge = review.querySelector('.jdgm-rev__buyer-badge');
      if (buyerBadge && this.verifiedLabel) {
        buyerBadge.textContent = this.verifiedLabel;
        buyerBadge.setAttribute('aria-label', this.verifiedLabel);
      }

      const reviewTitle = review.querySelector('.jdgm-rev__title');
      const reviewBody = review.querySelector('.jdgm-rev__body');
      const normalize = (value) => value.replace(/\s+/g, ' ').trim().toLocaleLowerCase(this.locale);
      const normalizedTitle = normalize(reviewTitle?.textContent || '');
      const normalizedBody = normalize(reviewBody?.textContent || '');
      const normalizedProductTitle = normalize(this.productTitle);
      const normalizedEmptyLabel = normalize(this.emptyReviewLabel);
      const containsOnlyProductTitle =
        !normalizedTitle
        && normalizedBody === normalizedProductTitle;
      const alreadyNormalizedRating =
        review.dataset.aveyRatingOnly === 'true'
        && !normalizedTitle
        && normalizedBody === normalizedEmptyLabel;
      const isRatingOnly = containsOnlyProductTitle || alreadyNormalizedRating;

      if (isRatingOnly && reviewBody && this.emptyReviewLabel) {
        review.dataset.aveyRatingOnly = 'true';
        review.classList.add('avey-review-card--rating-only');

        if (normalizedBody !== normalizedEmptyLabel) {
          const message = document.createElement('p');
          message.className = 'avey-review-card__empty-label';
          message.textContent = this.emptyReviewLabel;
          reviewBody.replaceChildren(message);
        }
      } else if (review.dataset.aveyRatingOnly === 'true') {
        delete review.dataset.aveyRatingOnly;
        review.classList.remove('avey-review-card--rating-only');
      }
      this.updateTranslationControl(review);
    });
  }

  enhancePagination() {
    const pagination = this.widget.querySelector('.jdgm-paginate');
    if (!pagination || pagination.dataset.aveyPaginationEnhanced === 'true') return;

    const pages = [...pagination.querySelectorAll('.jdgm-paginate__page[data-page]')]
      .map((link) => Number.parseInt(link.dataset.page, 10))
      .filter(Number.isFinite);
    const currentPage = Number.parseInt(
      pagination.querySelector('.jdgm-paginate__page.jdgm-curt')?.dataset.page || '1',
      10,
    );
    const lastPage = Number.parseInt(
      pagination.querySelector('.jdgm-paginate__last-page')?.dataset.page || String(Math.max(1, ...pages)),
      10,
    );

    if (!Number.isFinite(currentPage) || !Number.isFinite(lastPage)) return;

    const createButton = (page, label, direction) => {
      const button = document.createElement('button');
      button.className = `jdgm-paginate__page avey-reviews-pagination__button avey-reviews-pagination__button--${direction}`;
      button.type = 'button';
      button.dataset.page = String(page);
      button.disabled = page < 1 || page > lastPage;
      button.setAttribute('aria-label', label);
      button.title = label;
      button.textContent = direction === 'previous' ? '←' : '→';
      return button;
    };

    const status = document.createElement('span');
    status.className = 'avey-reviews-pagination__status';
    status.setAttribute('aria-current', 'page');
    status.textContent = this.pageStatusTemplate
      .replace('__CURRENT__', String(currentPage))
      .replace('__TOTAL__', String(lastPage));

    pagination.replaceChildren(
      createButton(currentPage - 1, this.previousLabel, 'previous'),
      status,
      createButton(currentPage + 1, this.nextLabel, 'next'),
    );
    pagination.dataset.aveyPaginationEnhanced = 'true';
    pagination.dataset.currentPage = String(currentPage);
    pagination.dataset.lastPage = String(lastPage);
    pagination.setAttribute('role', 'navigation');
    pagination.setAttribute('aria-label', this.paginationLabel);
  }

  observeJudgeMeUpdates() {
    this.observer = new MutationObserver((mutations) => {
      this.openRequestedReview();
      this.enhanceReviewForm();
      const hasRelevantUpdate = mutations.some((mutation) =>
        (mutation.type === 'attributes' &&
          mutation.target.matches?.('.jdgm-sort-dropdown-wrapper')) ||
        mutation.target.closest?.('.jdgm-rev') ||
        [...mutation.addedNodes].some(
          (node) =>
            node.nodeType === Node.ELEMENT_NODE &&
            (node.matches?.('.jdgm-rev, .jdgm-sort-dropdown-wrapper, .jdgm-paginate') ||
              node.querySelector?.('.jdgm-rev, .jdgm-sort-dropdown-wrapper, .jdgm-paginate')),
        ),
      );

      if (!hasRelevantUpdate || this.enhancementFrame) return;
      this.enhancementFrame = requestAnimationFrame(() => {
        this.enhancementFrame = null;
        this.enhanceReviews();
        this.enhancePagination();
        this.localizeDates();
      });
    });

    this.observer.observe(this.widget, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  async loadPage(page) {
    const pagination = this.widget.querySelector('.jdgm-paginate');
    const perPage = Number.parseInt(pagination?.dataset.perPage || '5', 10);
    if (!pagination || !Number.isFinite(perPage)) return;

    this.controller?.abort();
    this.controller = new AbortController();
    this.setLoading(true);

    const endpoint = this.reviewsEndpoint(page, perPage);

    try {
      const response = await fetch(endpoint, {
        credentials: 'omit',
        signal: this.controller.signal,
      });
      if (!response.ok) throw new Error(`Judge.me returned ${response.status}`);

      const payload = await response.json();
      if (typeof payload.html !== 'string') throw new Error('Judge.me returned no review HTML');

      const template = document.createElement('template');
      template.innerHTML = payload.html.trim();
      const nextReviews = template.content.querySelector('.jdgm-rev-widg__reviews');
      const nextPagination = template.content.querySelector('.jdgm-paginate');
      const reviews = this.widget.querySelector('.jdgm-rev-widg__reviews');

      if (!nextReviews || !nextPagination || !reviews) throw new Error('Judge.me review markup is incomplete');

      this.rememberOriginalReviews(nextReviews);
      reviews.replaceWith(nextReviews);
      pagination.replaceWith(nextPagination);
      this.initializeJudgeMeFeatures();
      this.enhanceReviews();
      this.enhancePagination();
      this.localizeDates();
      this.scrollToReviews();

      this.widget.dispatchEvent(
        new CustomEvent('avey:reviews:updated', {
          bubbles: true,
          detail: { page },
        }),
      );
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.warn('[Avey] Review page could not be loaded.', error);
      }
    } finally {
      this.setLoading(false);
    }
  }

  setLoading(isLoading) {
    const body = this.widget.querySelector('.jdgm-rev-widg__body');
    if (!body) return;

    if (this.heightReleaseFrame) {
      cancelAnimationFrame(this.heightReleaseFrame);
      this.heightReleaseFrame = null;
    }

    if (isLoading) {
      const currentHeight = Math.ceil(body.getBoundingClientRect().height);
      this.widget.style.setProperty('--avey-reviews-stable-height', `${currentHeight}px`);
    } else {
      this.heightReleaseFrame = requestAnimationFrame(() => {
        this.widget.style.removeProperty('--avey-reviews-stable-height');
        this.heightReleaseFrame = null;
      });
    }

    this.widget.classList.toggle('is-loading', isLoading);
    this.widget.setAttribute('aria-busy', String(isLoading));
    body.dataset.loadingLabel = this.loadingLabel;
  }

  localizeDates() {
    const formatter = new Intl.DateTimeFormat(this.locale, {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });

    this.widget.querySelectorAll('.jdgm-rev__timestamp[datetime]').forEach((timestamp) => {
      const date = new Date(timestamp.getAttribute('datetime'));
      if (!Number.isNaN(date.getTime())) {
        timestamp.textContent = formatter.format(date);
        timestamp.classList.remove('jdgm-spinner');
      }
    });
  }

  initializeJudgeMeFeatures() {
    if (!window.jdgm) return;

    window.jdgm.customizeReviews?.();
    if (window.jdgm.setupMediaGallery && window.jdgm.$) {
      window.jdgm.setupMediaGallery(window.jdgm.$(this.widget));
    }
  }

  configureJudgeMeScroll() {
    if (this.judgeMeScrollConfigured) return true;

    const settings = window.jdgmSettings;
    if (!settings || typeof settings.scrollTo !== 'function') return false;

    const defaultScroll = settings.scrollTo;
    settings.scrollTo = (target, offset) => {
      const element = target?.[0] || target;
      const isReviewsBody =
        element?.classList?.contains('jdgm-rev-widg__body') &&
        this.widget.contains(element);

      if (!isReviewsBody) return defaultScroll(target, offset);

      requestAnimationFrame(() => {
        this.enhanceReviews();
        this.scrollToReviews();
      });
      return false;
    };

    this.judgeMeScrollConfigured = true;
    return true;
  }

  scrollAfterRatingFilter() {
    if (this.filterScrollTimer) window.clearTimeout(this.filterScrollTimer);
    this.filterScrollTimer = window.setTimeout(() => {
      this.filterScrollTimer = null;
      this.enhanceReviews();
      this.scrollToReviews({ instant: true });
    }, 1400);
  }

  getReviewStickyOffset() {
    const tabs = this.widget.closest('avey-product-tabs');
    const tabsBar = tabs?.querySelector('.avey-product-tabs__bar');
    const panelHeading = this.widget
      .closest('[data-product-information-section]')
      ?.querySelector('.avey-product-tabs__heading');
    const tabsBarTop = Number.parseFloat(tabsBar ? getComputedStyle(tabsBar).top : '') || 0;
    const stickyOffset = tabsBarTop + (tabsBar?.getBoundingClientRect().height || 0);
    const headingHeight = panelHeading?.getBoundingClientRect().height || 0;

    return stickyOffset + headingHeight + 8;
  }

  scrollToReviews({ instant = false } = {}) {
    const body = this.widget.querySelector('.jdgm-rev-widg__body');
    const sortControl = this.widget.querySelector('.avey-reviews-sort');
    const scrollTarget = sortControl || body;
    if (!scrollTarget) return;

    const top =
      window.scrollY +
      scrollTarget.getBoundingClientRect().top -
      this.getReviewStickyOffset();
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: reducedMotion || instant ? 'auto' : 'smooth',
    });
  }
}

const pendingReviewConnections = new WeakMap();
const reviewObserver = 'IntersectionObserver' in window
  ? new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          pendingReviewConnections.get(entry.target)?.();
        });
      },
      { rootMargin: '1000px 0px' },
    )
  : null;

const initializeAveyProductReviews = (root = document) => {
  root.querySelectorAll('[data-product-reviews]').forEach((widget) => {
    if (
      widget.dataset.aveyReviewsInitialized === 'true'
      || pendingReviewConnections.has(widget)
    ) return;

    const connect = () => {
      if (widget.dataset.aveyReviewsInitialized === 'true') return;

      reviewObserver?.unobserve(widget);
      widget.removeEventListener('pointerdown', connect, true);
      widget.removeEventListener('focusin', connect, true);
      pendingReviewConnections.delete(widget);
      widget.dataset.aveyReviewsInitialized = 'true';
      new AveyProductReviews(widget).connect();
    };

    if (!reviewObserver || new URLSearchParams(window.location.search).get('review') === 'write') {
      connect();
      return;
    }

    pendingReviewConnections.set(widget, connect);
    widget.addEventListener('pointerdown', connect, { once: true, capture: true });
    widget.addEventListener('focusin', connect, { once: true, capture: true });
    reviewObserver.observe(widget);
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeAveyProductReviews(), { once: true });
} else {
  initializeAveyProductReviews();
}

document.addEventListener('shopify:section:load', (event) => initializeAveyProductReviews(event.target));
